import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'umi';
import * as LocationSelectors from '@/selectors/locationSelectors';
import ServiceTypeList from './ServiceTypeList';
import Drawer from '@/components/Drawer';
import { createServiceRequest } from '@/services/api/location';
import { processAPIResponseNow } from '@/utils/request';
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { Spin } from 'antd';

interface AddServiceOverlayProps {
  branch: any;
  onClose: (options?: { createdService?: any }) => void;
  open: boolean;
}

const AddServiceOverlay: React.FC<AddServiceOverlayProps> = ({ branch, onClose, open }) => {
  const dispatch = useDispatch();
  const serviceTypes = useSelector(LocationSelectors.selectServiceTypeList);
  const [loading, setLoading] = useState(false);

  if (!branch) return null;

  const { availableServiceTypes, addedServiceTypes } = useMemo(() => {
    const addedUuids = new Set(
      branch?.services
        ?.filter((s) => s.service_type?.uuid && !s.deleted_at)
        ?.map((s: any) => s.service_type?.uuid)
    );
    return {
      availableServiceTypes: serviceTypes.filter(st => !addedUuids.has(st.uuid)),
      addedServiceTypes: serviceTypes.filter(st => addedUuids.has(st.uuid)),
    };
  }, [serviceTypes, branch?.services]);

  const handleAddService = async (serviceTypeUuid: string) => {
    setLoading(true);

    const Response = await createServiceRequest({
      payload: {
        with_video_call: false,
        service_type: serviceTypeUuid,
        branch_id: branch.id,
        calendar: {
          process_type: "instant"
        }
      },
    });

    processAPIResponseNow(Response, {
      onSuccess: (data) => {
        const newService = data?.data || data;
        const services = branch.services || branch.branch_services || [];
        const updatedServices = [...services, newService];
        dispatch({
          type: 'location/setBranchDetail',
          payload: {
            branchId: branch.id!,
            branchData: {
              services: updatedServices,
              branch_services: updatedServices,
            },
          },
        });
        dispatch({
          type: 'location/getBranch',
          payload: {
            branchId: branch.id,
          },
        });
        onClose({ createdService: newService });
      },
      onEnd: () => {
        setLoading(false);
      },
    });
  };

  const handleCreateServiceType = () => {
    dispatch({
      type: 'location/toggleServiceTypeDrawer',
      payload: {
        mode: 'create',
        onCreate(serviceType) {
          handleAddService(serviceType.uuid);
        },
      },
    });
  };

  return (
    <Drawer
      open={open}
      title={<F entity={INTL_ENTITY.SERVICE} action={INTL_ACTION.ADD} />}
      onOpenChange={onClose}
    >
      <Spin spinning={loading || false}>
        <ServiceTypeList
          availableServiceTypes={availableServiceTypes}
          addedServiceTypes={addedServiceTypes}
          onAddService={handleAddService}
          onCreateServiceType={handleCreateServiceType}
        />
      </Spin>
    </Drawer>
  );
};

export default AddServiceOverlay;
